import React, { useState, useEffect } from "react";
import { 
  RocketLaunchIcon, 
  ShareIcon, 
  BanknotesIcon,
  SparklesIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ClockIcon,
  ArrowTrendingUpIcon
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolidIcon } from "@heroicons/react/24/solid";

const WorkFlow = () => {
  const [activeStep, setActiveStep] = useState(1);

  // Auto-rotate steps every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prevStep) => (prevStep % 3) + 1);
    }, 2000);
    
    return () => clearInterval(interval);
  }, []);

  const steps = [
    {
      number: "01",
      title: "Start a free fundraiser",
      description: "Create campaign in minutes. Add photos, tell your story, and set your goal.",
      gradient: "from-teal-600 to-emerald-600",
      lightGradient: "from-teal-50 to-emerald-50",
      icon: RocketLaunchIcon,
      preview: {
        heading: "Create your campaign",
        subtext: "Tell your story in minutes",
        features: ["Add photos", "Set goal", "Write story"]
      }
    },
    {
      number: "02",
      title: "Share your story",
      description: "Share with friends & family. Built-in tools help you reach more people.",
      gradient: "from-blue-600 to-indigo-600",
      lightGradient: "from-blue-50 to-indigo-50",
      icon: ShareIcon,
      preview: {
        heading: "Spread the word",
        subtext: "Reach more supporters",
        features: ["WhatsApp", "Facebook", "Twitter"]
      }
    },
    {
      number: "03",
      title: "Withdraw anytime",
      description: "Funds transfer directly to your bank. No minimum, no waiting, no fees.",
      gradient: "from-amber-600 to-orange-600",
      lightGradient: "from-amber-50 to-orange-50",
      icon: BanknotesIcon,
      preview: {
        heading: "Instant withdrawal",
        subtext: "Money in your account",
        features: ["No minimum", "24/7 access", "Zero fees"]
      }
    },
  ];

  // Get current step icon
  const CurrentStepIcon = steps[activeStep - 1]?.icon || RocketLaunchIcon;

  return (
    <section className="relative bg-white py-12 md:py-20 lg:py-24 overflow-hidden">
      {/* Premium minimal background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-2/3 h-2/3 bg-gradient-to-br from-teal-50/30 to-transparent rounded-bl-full"></div>
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-emerald-50/30 to-transparent rounded-tr-full"></div>
      </div>
      
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, #0d9488 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }}></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header - Minimal, elegant */}
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-gray-50 text-gray-600 text-xs md:text-sm font-medium rounded-full border border-gray-100 mb-3 md:mb-4">
            <SparklesIcon className="w-3.5 h-3.5 md:w-4 md:h-4 text-teal-500" />
            Simple, transparent, free
          </span>
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-semibold text-gray-900 tracking-tight">
            Fundraising in{' '}
            <span className="text-teal-600 relative">
              three steps
              <span className="absolute -bottom-2 left-0 w-full h-0.5 bg-teal-200"></span>
            </span>
          </h2>
        </div>

        {/* Main Content - Two column layout */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-center">
          
          {/* LEFT - Premium Timeline Steps with Icons */}
          <div className="flex-1 space-y-5 lg:space-y-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = activeStep === index + 1;
              
              return (
                <div
                  key={index}
                  className={`relative flex items-start gap-4 lg:gap-6 transition-all duration-700 ${
                    isActive ? 'lg:scale-[1.02]' : ''
                  }`}
                >
                  {/* Active state indicator with pulse animation */}
                  {isActive && (
                    <>
                      <div className="absolute -left-3 lg:-left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-teal-500 to-emerald-500 rounded-full animate-pulse"></div>
                      <div className="absolute -left-4 lg:-left-8 top-1/2 -translate-y-1/2 w-1.5 h-1.5 lg:w-2 lg:h-2 bg-teal-500 rounded-full animate-ping"></div>
                    </>
                  )}
                  
                  {/* Number with Icon - Clean, sophisticated */}
                  <div className="relative flex-shrink-0">
                    <div className={`relative w-10 h-10 lg:w-16 lg:h-16 rounded-xl lg:rounded-2xl flex items-center justify-center transition-all duration-500 ${
                      isActive
                        ? `bg-gradient-to-br ${step.gradient} text-white shadow-lg shadow-teal-100 scale-110`
                        : 'bg-white border border-gray-200 text-gray-400'
                    }`}>
                      {isActive ? (
                        <Icon className="w-5 h-5 lg:w-7 lg:h-7" />
                      ) : (
                        <span className="text-sm lg:text-2xl font-medium">
                          {step.number}
                        </span>
                      )}
                    </div>
                    
                    {/* Connection line - except for last step */}
                    {index < steps.length - 1 && (
                      <div className={`absolute top-11 lg:top-16 left-1/2 -translate-x-1/2 w-px h-6 lg:h-10 bg-gradient-to-b transition-all duration-500 ${
                        isActive || activeStep > index + 1 
                          ? 'from-teal-400 to-teal-200' 
                          : 'from-gray-200 to-transparent'
                      }`}></div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 pt-1 lg:pt-2">
                    <h3 className={`text-sm lg:text-xl font-semibold mb-1 lg:mb-2 transition-all duration-500 ${
                      isActive
                        ? 'text-gray-900 lg:translate-x-1'
                        : 'text-gray-600'
                    }`}>
                      {step.title}
                    </h3>
                    <p className={`text-xs lg:text-base leading-relaxed transition-all duration-500 ${
                      isActive
                        ? 'text-gray-500 lg:text-gray-600'
                        : 'text-gray-400'
                    }`}>
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
            
            {/* Time indicator - Hidden on mobile, visible on desktop */}
            <div className="hidden lg:flex items-center gap-3 pt-3">
              <div className="flex -space-x-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-white flex items-center justify-center">
                  <ClockIcon className="w-3.5 h-3.5 text-gray-600" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-white flex items-center justify-center">
                  <span className="text-xs font-medium text-gray-600">5m</span>
                </div>
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-white flex items-center justify-center">
                  <span className="text-xs font-medium text-gray-600">10m</span>
                </div>
              </div>
              <span className="text-sm text-gray-500">
                Average setup time: <span className="font-medium text-gray-900">2 minutes</span>
              </span>
            </div>
          </div>

          {/* RIGHT - Realistic Smartphone Mockup with Video */}
          <div className="flex-1 flex justify-center lg:justify-end">
            <div className="relative">
              {/* Realistic Phone Mockup */}
              <div className="relative w-[260px] md:w-[320px]">
                {/* Phone frame - Premium smartphone design */}
                <div className="relative bg-black rounded-[36px] p-1.5 shadow-2xl">
                  {/* Screen with curved edges */}
                  <div className="relative bg-white rounded-[32px] overflow-hidden h-[540px] md:h-[640px]">
                    
                    {/* Dynamic Island / Notch */}
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-[90px] md:w-[110px] h-[26px] md:h-[28px] bg-black rounded-b-2xl z-20 flex items-center justify-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-gray-800"></div>
                      <div className="w-7 md:w-9 h-2 bg-gray-800 rounded-full"></div>
                    </div>
                    
                    {/* Status bar */}
                    <div className="relative z-10 pt-6 md:pt-7 px-4 md:px-5 flex justify-between items-center">
                      <span className="text-xs font-semibold text-gray-900">9:41</span>
                      
                    </div>
                    
                    {/* Video Container - Full screen on mobile */}
                    <div className="h-full bg-gray-50">
                      <video
                        src="/assets/Donatepe record.mp4"
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Overlay with step information - Hidden on mobile */}
                      <div className="hidden lg:block absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent">
                        <div className="absolute bottom-5 left-5 text-white">
                          <div className={`flex items-center gap-2 mb-1.5 transition-all duration-500 ${
                            activeStep === 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                          }`}>
                            <RocketLaunchIcon className="w-5 h-5 text-teal-400" />
                            <span className="text-sm font-medium">Step 1: Start fundraiser</span>
                          </div>
                          <div className={`flex items-center gap-2 mb-1.5 transition-all duration-500 delay-100 ${
                            activeStep === 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                          }`}>
                            <ShareIcon className="w-5 h-5 text-blue-400" />
                            <span className="text-sm font-medium">Step 2: Share story</span>
                          </div>
                          <div className={`flex items-center gap-2 transition-all duration-500 delay-200 ${
                            activeStep === 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                          }`}>
                            <BanknotesIcon className="w-5 h-5 text-amber-400" />
                            <span className="text-sm font-medium">Step 3: Withdraw funds</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Bottom navigation */}
                    <div className="absolute bottom-3 md:bottom-4 left-0 right-0 flex justify-center gap-5">
                      <div className={`h-1 rounded-full transition-all duration-300 ${
                        activeStep === 1 ? 'w-4 bg-teal-500' : 'w-1 bg-gray-300'
                      }`}></div>
                      <div className={`h-1 rounded-full transition-all duration-300 ${
                        activeStep === 2 ? 'w-4 bg-blue-500' : 'w-1 bg-gray-300'
                      }`}></div>
                      <div className={`h-1 rounded-full transition-all duration-300 ${
                        activeStep === 3 ? 'w-4 bg-amber-500' : 'w-1 bg-gray-300'
                      }`}></div>
                    </div>
                  </div>
                  
                  {/* Side buttons (volume, power) */}
                  <div className="absolute -left-1 top-20 w-1 h-7 bg-gray-700 rounded-l-lg"></div>
                  <div className="absolute -left-1 top-32 w-1 h-7 bg-gray-700 rounded-l-lg"></div>
                  <div className="absolute -right-1 top-28 w-1 h-10 bg-gray-700 rounded-r-lg"></div>
                </div>
                
                {/* Floating stats card - Hidden on mobile */}
                
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Trust Bar - Clean, minimal */}
        <div className="mt-12 md:mt-16 flex flex-wrap items-center justify-center gap-5 md:gap-6 text-xs md:text-sm text-gray-500">
          <div className="flex items-center gap-1.5 md:gap-2">
            <ShieldCheckIcon className="w-3.5 h-3.5 md:w-4 md:h-4 text-teal-500" />
            <span>Secure & encrypted</span>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2">
            <BanknotesIcon className="w-3.5 h-3.5 md:w-4 md:h-4 text-teal-500" />
            <span>Zero platform fees</span>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2">
            <UserGroupIcon className="w-3.5 h-3.5 md:w-4 md:h-4 text-teal-500" />
            <span>50,000+ campaigners</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkFlow;